/**
 * Chọn khách để "Chạy thử" luồng đang ở canvas. Chạy thử chỉ MÔ PHỎNG ở
 * backend: không gửi tin, không ghi dữ liệu khách, khối Chờ được tua qua.
 */
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FlaskConical, Loader2, Search, ShieldCheck, UserRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useContacts, stageLabel } from '@/hooks/use-contacts'
import { triggerMeta } from '@/lib/automation/catalog'

export interface KhachThu { id: string; ten: string }

interface Props {
  open: boolean
  onClose: () => void
  triggerType: string
  dangChay: boolean
  /** undefined = dùng dữ liệu mẫu. */
  onChay: (khach?: KhachThu) => void
}

export function ChayThuDialog({ open, onClose, ...rest }: Props) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      {/* Nội dung chỉ mount khi mở → chỉ tìm khách khi cần. */}
      <DialogContent className="max-w-lg"><NoiDung {...rest} /></DialogContent>
    </Dialog>
  )
}

function NoiDung({ triggerType, dangChay, onChay }: Omit<Props, 'open' | 'onClose'>) {
  const { t } = useTranslation('autoTemplates')
  const [q, setQ] = useState('')
  const [tim, setTim] = useState('')
  const [chon, setChon] = useState<KhachThu | null>(null)
  useEffect(() => { const h = setTimeout(() => setTim(q.trim()), 300); return () => clearTimeout(h) }, [q])
  const { data, isFetching } = useContacts({ search: tim || undefined, limit: 8 })
  const tm = triggerMeta(triggerType)

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><FlaskConical className="h-4 w-4" /> {t('chayThu.title')}</DialogTitle>
        <DialogDescription>{t('chayThu.description')}</DialogDescription>
      </DialogHeader>
      <p className="flex items-start gap-2 rounded-md border border-success/40 bg-success/10 p-2.5 text-[12px]">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" />
        <span><Trans t={t} i18nKey="chayThu.simNote" components={{ b: <b /> }} />{tm.coHoiThoai ? t('chayThu.simNoteChat') : ''}</span>
      </p>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('chayThu.searchPlaceholder')} className="pl-8" />
        {isFetching && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      <div className="max-h-64 space-y-1 overflow-y-auto">
        {(data?.contacts ?? []).map((c) => {
          const ten = c.fullName || c.crmName || c.phone || t('chayThu.unnamed')
          return (
            <button key={c.id} type="button" onClick={() => setChon({ id: c.id, ten })}
              className={cn('flex w-full items-center gap-2 rounded-md border p-2 text-left text-sm hover:bg-accent/50', chon?.id === c.id && 'border-primary bg-primary/5')}>
              <UserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1"><span className="block truncate font-medium">{ten}</span><span className="block truncate text-[11px] text-muted-foreground">{[c.phone, (c.lifecycleStage ? stageLabel(c.lifecycleStage) : c.lifecycleStage)].filter(Boolean).join(' · ')}</span></span>
            </button>
          )
        })}
        {data && data.contacts.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">{t('chayThu.noMatch')}</p>}
      </div>
      <DialogFooter className="gap-2 sm:justify-between">
        <Button variant="ghost" disabled={dangChay} onClick={() => onChay(undefined)}>{t('chayThu.useSample')}</Button>
        <Button disabled={!chon || dangChay} onClick={() => chon && onChay(chon)}>
          {dangChay ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} {chon ? t('chayThu.runWith', { name: chon.ten }) : t('chayThu.run')}
        </Button>
      </DialogFooter>
    </>
  )
}
